/**
 * Hooks para el módulo Cupones — Fase 4.4
 *
 * Todas las operaciones van directo a Supabase (decisión #014).
 * La generación del cupón ocurre automáticamente en useConfirmarDDJJ (useDDJJ.ts).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Cupon } from '../types/cupon';

// ── Query Keys ────────────────────────────────────────────────────────────────

export const QK_CUPONES = {
  all:    ['cupones'] as const,
  list:   (empresaId?: string, periodoId?: string) =>
    ['cupones', 'list', empresaId ?? 'all', periodoId ?? 'all'] as const,
  detail: (cuponId?: string) => ['cupones', 'detail', cuponId] as const,
};

// ── JOINS estándar ────────────────────────────────────────────────────────────

const CUPON_SELECT =
  '*, empresa:empresas(id, razon_social, cuit), periodo:periodos(id, periodo, descripcion), planilla:planillas_ddjj(id, numero)';

// ── LIST ──────────────────────────────────────────────────────────────────────

/**
 * Lista cupones con joins a empresa, periodo y planilla.
 * Filtros opcionales por empresa y/o período.
 */
export function useCupones(empresaId?: string, periodoId?: string) {
  return useQuery({
    queryKey: QK_CUPONES.list(empresaId, periodoId),
    queryFn: async () => {
      let query = supabase
        .from('cupones')
        .select(CUPON_SELECT)
        .order('created_at', { ascending: false });

      if (empresaId) query = query.eq('empresa_id', empresaId);
      if (periodoId) query = query.eq('periodo_id', periodoId);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Cupon[];
    },
  });
}

// ── SINGLE ────────────────────────────────────────────────────────────────────

/** Obtiene un cupón por ID con todos sus joins. */
export function useCupon(cuponId: string | undefined) {
  return useQuery({
    queryKey: QK_CUPONES.detail(cuponId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cupones')
        .select(CUPON_SELECT)
        .eq('id', cuponId!)
        .single();
      if (error) throw error;
      return data as Cupon;
    },
    enabled: !!cuponId,
  });
}

// ── BY PLANILLA (cupón vigente — excluye anulados) ────────────────────────────

/**
 * Devuelve el cupón vigente asociado a una planilla, o null si no hay.
 * Excluye cupones anulados (caso reapertura — ver migration de 2026-05-21).
 */
export function useCuponPorPlanilla(planillaId: string | undefined) {
  return useQuery({
    queryKey: ['cupones', 'by-planilla', planillaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cupones')
        .select(CUPON_SELECT)
        .eq('planilla_id', planillaId!)
        .neq('estado', 'anulado')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Cupon | null;
    },
    enabled: !!planillaId,
  });
}

// ── RECÁLCULO DE MORA (Fase 7 — decisión #029) ────────────────────────────────

/**
 * Recálculo singular del interés de un cupón (on-demand).
 * Pensado para usar al abrir el detalle: actualiza el cupón y devuelve el
 * resumen del cambio. Si el cupón está pagado/anulado, devuelve changed=false.
 */
export function useRecalcularInteresesCupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cuponId: string) => {
      const { data, error } = await supabase.rpc('recalcular_intereses_cupon', {
        p_cupon_id: cuponId,
      });
      if (error) throw error;
      return data as {
        cupon_id: string;
        changed: boolean;
        dias_mora?: number;
        coef_diario?: number;
        interes_anterior?: number;
        interes?: number;
        estado_anterior?: string;
        estado?: string;
        razon?: string;
      };
    },
    onSuccess: (resultado) => {
      // Solo invalida si hubo cambio efectivo
      if (resultado.changed) {
        qc.invalidateQueries({ queryKey: QK_CUPONES.detail(resultado.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.all });
      }
    },
  });
}

/**
 * Recálculo masivo (botón admin). Llama al endpoint backend que ejecuta el
 * RPC con service_role para garantizar acceso a todos los cupones.
 */
import { api } from '../services/api';

export function useRecalcularInteresesMasivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        data: { revisados: number; cambiados: number; ejecutado_at: string };
      }>('/cupones/recalcular-intereses');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_CUPONES.all });
    },
  });
}

// ── REIMPRIMIR ────────────────────────────────────────────────────────────────

/**
 * Incrementa el contador de reimpresiones de un cupón.
 * No cambia el estado ni ningún otro campo.
 *
 * Va por el RPC `incrementar_reimpresion_cupon` (SECURITY DEFINER) en vez de un
 * UPDATE directo: las policies RLS de `cupones` solo dan SELECT al rol empresa,
 * así que un UPDATE directo afectaba 0 filas y el `.single()` lanzaba PGRST116
 * al descargar el PDF (ticket #490 reabierto). El RPC valida el acceso
 * (admin/operador o la empresa dueña) sin ampliar RLS.
 */
export function useReimprimirCupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cuponId: string) => {
      const { data, error } = await supabase.rpc('incrementar_reimpresion_cupon', {
        p_cupon_id: cuponId,
      });
      if (error) throw error;
      return data as Cupon;
    },
    onSuccess: (cupon) => {
      qc.invalidateQueries({ queryKey: QK_CUPONES.detail(cupon.id) });
      qc.invalidateQueries({ queryKey: QK_CUPONES.all });
    },
  });
}
